const SubscriptionService = require("Services/SubscriptionService");
const ProductService = require("Services/ProductService");
import MixpanelService from "Services/MixpanelService";

import helpers from "Modules/SubscriptionHelper";
import DoughnutChartHelper from "Modules/DoughnutChartHelper";
import BaseBox from "Components/ui/BaseBox";
import BaseDog from "Components/ui/BaseDog";
import HeaderBanner from "Components/layout/HeaderBanner.vue";
import DoughnutChart from "Components/ui/DoughnutChart.vue";
import UpdateModals from "Components/forms/UpdateModals.vue";
import BaseLoading from "Components/ui/BaseLoading";
import StandardTemplate from "Components/layout/StandardTemplate";

export default {
  name: 'AdminDashboard',
  components: {
    BaseBox,
    BaseDog,
    BaseLoading,
    HeaderBanner,
    DoughnutChart,
    UpdateModals,
    StandardTemplate
  },
  data () {
    // evaluate routing data for page rendering
    const queryParams = this.$store.getters.getLandingData.query;
    if (queryParams) {
      // user is back on the page after a successful redirect from 3ds authorization
      if (queryParams.threeds_success === "true") {
        MixpanelService.Track("AdminPortal:3dsSuccess", { "subscription_id": this.$store.getters.getSubscriptionId, "page": "Dashboard" });
        this.$toast.success({ message: "Congratulations on your new subscription! You'll find all your updated subscription information below" });
      }
      this.$store.commit("clear_landing");
    }
    return {
      subscription: undefined,
      users: {},
      billingInfo: { address: "", address2: "", city: "", zip: "", country: "", state: "", firstName: "", lastName: "" },
      chartData: undefined,
      enabledUsers: 0,
      pendingUsers: 0,
      remainingSlots: 0,
      exceededInvites: 0,
      dictationUsers: 0,
      nonDictationUsers: 0,
      loading: true,

      // updateModal properties
      COUNTRY_ISO_MAP: require("@/assets/content/country_iso_map.json"),
      STATE_ISO_MAP: require("@/assets/content/state_iso_map.json"),
      productList: [],
      userComponentsPP: undefined,
      verifiedComponentsPP: undefined,
      countryList: [],
      subscriptionModalDisplay: false,
      billingModalDisplay: false,
      threeDSecureUpgradeModalDisplay: false,
      // async loading for products
      loadingProduct: true,

    };
  },

  created () {
    this.countryList = helpers.setCountryList(this.COUNTRY_ISO_MAP, this.STATE_ISO_MAP);

    const coreDataPromise = SubscriptionService.GetSubscription()
      .then((results) => {
        // setting required page data
        this.subscription = results.data;
        this.loading = false;
        this.updatePageRender();
      })
      .catch((e) => {
        this.$toast.error({ message: "There was an issue getting your subscription information!" });
        console.log("failed to get page data:" + e);
      });
    // break-out product request, not needed for the core functionality of the page.
    if (this.userPerms.billing_management) {
      let billingPromise = Promise.all([ProductService.GetProducts(), ProductService.GetComponentUsersPricePoints(), ProductService.GetComponentVerifiedPricePoints()])
        .then(resp => {
          this.productList = resp[0].data.products;
          this.userComponentsPP = resp[1].data.component_price_points;
          this.verifiedComponentsPP = resp[2].data.component_price_points;
        })
        .catch(err => {
          console.log(err);
        });

      Promise.allSettled([coreDataPromise, billingPromise]).then(() => {
        this.loadingProduct = false;
      });
    }
  },

  computed: {
    isNewProduct () {
      if (!this.subscription) return false;
      switch (this.subscription.product.handle) {
        case ("talkatoo_monthly"):
        case ("talkatoo_annual"):
        case ("solo"):
        case ("duo"):
        case ("group"): return true;
        default: return false;
      }
    },

    paymentMessage () {
      if (!this.subscription) return "";
      if (this.subscription.subscription_state == "cancelled") {
        return "Your subscription is cancelled";
      }
      const price = this.nextBillingAmount;
      const period = this.subscription.product.interval_months === '12' ? 'annual' : 'monthly';
      return `Next ${period} payment of ${price}`;
    },
    // only rendered on user accounts having enough credits to be billed 0 dollars
    chargeMessage () {
      if (!this.subscription) return "";
      const period = this.subscription.product.interval_months === '12' ? 'annual' : 'monthly';
      return `Estimate ${period} plan cost: ${this.estimatedCharge}`;
    },
    // provides an estimated charge on the next billing period of the plan
    userComponentsCost () {
      let cost = 0;
      const subPricePointHandle = this.subscription.product.price_points[0].handle;
      const usersComponent =
        this.subscription.components.find(c => c.chargify_component_handle === "users"
          && c.chargify_component_price_point_handle === subPricePointHandle);
      if (usersComponent) {
        let usersPP = this.userComponentsPP?.find(c => c.id == usersComponent.chargify_component_price_point_id);
        let ppPrice = 0;
        if (usersPP) {

          let usdUsersPrice = usersPP.prices.find(x => x.starting_quantity == this.subscription.product.number_of_users);
          if (usdUsersPrice === undefined) {
            // defaulting to cheapest price when selected user count isn't isn't fround. presuming that the subscription has > 15 users and every subsuquent user will cost the cheapest price
            usdUsersPrice = usersPP.prices.find(x => x.starting_quantity == 15);
          }
          if (this.subscription.currency == "USD") {
            // backwards support for old price points. Accounts will be migrated from this so after 1.21.0 release we can remove this ig
            if (subPricePointHandle === "standard" || subPricePointHandle === "premium") {
              ppPrice = parseInt(usersPP.prices[0].unit_price);
            } else {
              // usdUsersPrice = usersPP.prices.find(x => x.starting_quantity == this.subscription.product.number_of_users);
              ppPrice = parseInt(usdUsersPrice.unit_price);
            }
          }
          else {
            let currencyPrice = usersPP.currency_prices.find(x => x.currency == this.subscription.currency && x.price_id === usdUsersPrice.id);
            if (currencyPrice === undefined) {
              console.warn("Could not find currency price for subscription's currency and selected seat count", this.subscription.currency, usersPP);
            }
            ppPrice = parseInt(currencyPrice.price);
            // }
          }
          // componentCurrencyPrice.price is in dollars
          cost = ppPrice * parseInt(usersComponent.component_allocation_count);
        }
      }
      return cost;
    },
    verifiedComponentsCost () {
      let cost = 0;
      const verifiedComponent = this.subscription.components.find(c => c.chargify_component_handle === "verified");
      if (verifiedComponent) {
        let component = this.verifiedComponentsPP?.find(c => c.id == verifiedComponent.chargify_component_price_point_id);
        let ppPrice = 0;
        if (component) {
          if (this.subscription.currency == "USD") {
            ppPrice = parseInt(component.prices[0].unit_price);
          } else {
            let componentCurrencyPrice = component.currency_prices.find(x => x.currency === this.subscription.currency);
            ppPrice = parseInt(componentCurrencyPrice.price);
          }
          // componentCurrencyPrice.price is in dollars
          cost = ppPrice * parseInt(verifiedComponent.component_allocation_count);
        }
      }
      return cost;
    },
    estimatedCharge () {
      if (!this.subscription) return '';
      const price = this.$store.getters.currencyFormatter.format(this.userComponentsCost + this.verifiedComponentsCost + this.subscription.product.price_in_cents / 100);
      return `${price}`;
    },
    nextBillingAmount () {
      if (!this.subscription) return '';
      return this.$store.getters.currencyFormatter.format(this.subscription.current_billing_amount_in_cents / 100);
    },
    cardExists () {
      return !!this.subscription?.payment_profile.masked_card_number;
    },
    userPerms () {
      return this.$store.getters.getUserPerms;
    },
    nextBillingDate () {
      if (!this.subscription) return "";
      var tmpDate = new Date(this.subscription.payment_profile.next_billing_at).toString();
      return tmpDate.substr(4, 6) + ", " + tmpDate.substr(11, 4);
    }
  },
  methods: {
    updatePageRender () {
      // defines the billing address for viewing pleasure
      if (this.subscription && this.$store.getters.getUserPerms.billing_management) {
        this.setBillingAddress(this.subscription?.payment_profile);
      }
      // initializing data into a format that works with the table component
      this.buildDoughnutChart();
    },

    // the following is used to copy billing address for mutation, and setting default values when a user is trialing
    setBillingAddress ({ first_name, last_name, billing_address }) {
      helpers.setBillingAddress.call(this, billing_address);
    },

    buildDoughnutChart () {
      DoughnutChartHelper.buildDoughnutChart.call(this, this.subscription);
    },

    //expects price in cents
    commaDelineatedPrice (price_in_cents) {
      var price = `$${this.$store.getters.currencyFormatter.format(parseInt(price_in_cents) / 100)} ${this.subscription.currency}`;
      return price;
    },

    // update modal event handlers
    toggleSubscriptionModal (e) {
      this.subscriptionModalDisplay = !this.subscriptionModalDisplay;
    },

    toggleBillingModal () {
      this.billingModalDisplay = !this.billingModalDisplay;
    },

    toggleThreeDSecureModal () {
      this.threeDSecureUpgradeModalDisplay = !this.threeDSecureUpgradeModalDisplay;
    },
    reactivateSubscription () {
      this.loading = true;
      SubscriptionService.ReactivateSubscription()
        .then(resp => {
          this.subscription = resp.data;
          this.$toast.success({ message: "Congrats on reactivating your subscription! Start saving time with Talkatoo!" });
        }).catch(err => {
          console.log(err);
          this.$toast.error({ message: "Reactivating your subscription failed! Please get in touch with support to receive help" });
        }).finally(_ => {
          this.loading = false;
        });
    },
    // incoming new sub from Update modals. triggered on completed form submissions
    // event handler
    subscriptionUpdate (sub) {
      this.loading = true;
      this.subscription = sub;
      this.updatePageRender();
      this.loading = false;
    },
    setLoading (bool) {
      this.loading = bool;
    },
    pendingRequest () {
      this.loading = true;
    },
    handle3ds (threeDSecureErr) {
      this.threeDSecureRedirect = helpers.handle3dsError.call(this, threeDSecureErr);
      this.loading = false;
      this.threeDSecureUpgradeModalDisplay = true;
    },
  },
};